import { useFormik } from 'formik';
import Button from '../../components/button/Button';
import Card from '../../components/card/Card';
import DropDown from '../../components/dropdown/DropDown';
import Input from '../../components/input/Input';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import { formatCurrency } from '../../utils/Masks';
import { useEffect, useState } from 'react';
import Icon from '../../components/icon/Icon';
import Span from '../../components/texts/span/Span';
import { getProductLines, getCreditTypes } from '../../services/ClientService';
import { useClientTheme } from '../../context/ThemeContext';
import DatePicker from '../../components/datepicker/DatePicker';
import {
  amount,
  applicationData,
  clientData,
  signersData,
} from '../../types/interfaces';
import Modal from '../../components/modal/modal';
import Spinner from '../../components/Spinner/spinner';
import Toggle from '../../components/toggle/Toggle';
import { APPLICATION_STATUS, CURRENCY_CODES } from '../../utils/Constants';
import TextArea from '../../components/textarea/TextArea';
import useStatus from '../../hooks/useStatus';
import Signers from '../common/Signers';
import { toNumber } from '../../utils/Utils';
import { NumericFormat } from 'react-number-format';

interface ApplicationProps {
  applications: any;
  cuit: string;
  id: string | undefined;
  onSave: Function;
  applicationStatus: number;
  onPreviousClick: Function;
  isSaving: boolean;
  manualTotalAmounts: boolean;
  requestedAmounts: amount[];
  reason?: string;
  review?: string;
  extraComment?: string;
  hideInfo?: boolean;
  onChange?: Function;
  clientData: clientData;
  signers: signersData[];
}

const Application: React.FC<ApplicationProps> = ({
  cuit,
  id,
  onSave,
  applications,
  applicationStatus,
  onPreviousClick,
  isSaving,
  manualTotalAmounts,
  requestedAmounts,
  reason,
  review,
  extraComment,
  hideInfo = false,
  onChange,
  clientData,
  signers,
}) => {
  const { clientTheme } = useClientTheme();
  const { isApproved, isRejected, isInCommittee, isFinished, isInInstrumentation } = useStatus({
    applicationStatus,
  });
  
  const config = JSON.parse(localStorage.getItem('config') ?? '{}');
  const isApplicationEffectiveDateEnabled = config?.isApplicationEffectiveDateEnabled ?? false;
  const isApplicationCreditTypeEnabled = config?.isApplicationCreditTypeEnabled ?? false;
  const [applicationInfo, setApplicationInfo] = useState<applicationData[]>([
    {
      id: 0,
      product_line_code: '001',
      amount: {
        currency_code: CURRENCY_CODES.ars,
        value: 0,
      },
    },
  ]);
  const [_isShouldContinue, setIsShouldContinue] = useState<boolean>(false);
  const [totalArs, setTotalArs] = useState<number>(0);
  const [totalUsd, setTotalUsd] = useState<number>(0);
  const [productLines, setProductLines] = useState<any>();
  const [creditTypes, setCreditTypes] = useState<any>();
  const [isDeleteApplicationOpen, setIsdeleteApplication] =
    useState<boolean>(false);
  const [productToDelete, setProductToDelete] = useState<number>(0);
  const [_modalText, setModalText] = useState<string>('');
  const [summarizeAmounts, setsummarizeAmounts] =
    useState<boolean>(!manualTotalAmounts);
  const [hasErrors, setHasErrors] = useState<boolean>(true);
  const [clientInfo, setClientInfo] = useState<clientData>(clientData);
  const [signersUploadFunction, setSignersUploadFunction] = useState<(() => Promise<void>) | null>(null);
  const [isUploadingSignersFiles, setIsUploadingSignersFiles] = useState<boolean>(false);
  const [manualAmounts, setManualAmounts] = useState<amount[]>([
    {
      currency_code: CURRENCY_CODES.ars,
      value: 0,
    },
    {
      currency_code: CURRENCY_CODES.usd,
      value: 0,
    },
  ]);

  const handleInputChange = (currencyCode: string, value: number | string) => {
    setManualAmounts((prev) =>
      prev.map((amount) =>
        amount.currency_code === currencyCode
          ? {
              ...amount,
              value: value === '' ? '' : toNumber(value),
            }
          : amount
      )
    );
  };
  useEffect(() => {
    if (!productLines) {
      getProductLines().then((response) => {
        if (response) {
          setProductLines(response.data);
        }
      });
    }
    if (!creditTypes) {
      getCreditTypes().then((response) => {
        if (response) {
          setCreditTypes(response.data);
        }
      });
    }
  }, [productLines, creditTypes]);

  const creditTypeItems = creditTypes?.map((creditType: any) => ({
    label: creditType.name,
    disabled: false
  })) || [];

  const getSelectedCreditType = () => {
    const creditType = creditTypes?.find((ct: any) => 
      ct.id === applicationDataFormik.values.credit_type_id || 
      ct.name === applicationDataFormik.values.credit_type_name
    );
    return creditType?.name || 'Seleccionar';
  };

  const handleCreditTypeChange = (selectedLabel: string) => {
    const creditType = creditTypes?.find((ct: any) => ct.name === selectedLabel);
    if (creditType) {
      applicationDataFormik.setFieldValue('credit_type_id', creditType.id);
      applicationDataFormik.setFieldValue('credit_type_name', creditType.name);
    }
  };

  const validateProductData = (values: applicationData[]) => {
    const errors: any[] = [];

    values.forEach(({ amount }, index) => {
      const productErrors: any = {};
      if (!amount.value) {
        productErrors.amount = "Debes completar el campo 'Monto'";
      }
      if (amount.value === 0) {
        productErrors.amount = 'El monto debe ser mayor a 0';
      }
      if (Object.keys(productErrors).length > 0) {
        errors[index] = productErrors;
      }

      const hasErrors = errors.some((error) => Object.keys(error).length > 0);
      setIsShouldContinue(!hasErrors);
    });
    return errors;
  };

  const productFormik = useFormik({
    initialValues: applications || applicationInfo,
    validate: validateProductData,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: (values) => {},
  });

  const applicationDataFormik = useFormik({
    initialValues: {
      reason: reason,
      review: review,
      extraComment: extraComment,
      effective_date: clientData?.effective_date || '',
      credit_type_id: clientData?.credit_type_id || null,
      credit_type_name: clientData?.credit_type_name || '',
    },

    enableReinitialize: true,
    onSubmit: (values) => {},
  });

  useEffect(() => {
    updateTotals();
    productFormik.validateForm();
    onChange &&
      onChange({
        applications: productFormik.values,
        manualTotalAmounts: !summarizeAmounts,
        requestedAmounts: manualAmounts,
      });
  }, [productFormik.values]);

  useEffect(() => {
    onChange &&
      onChange({
        applications: productFormik.values,
        manualTotalAmounts: !summarizeAmounts,
        requestedAmounts: manualAmounts,
      });
  }, [manualAmounts, summarizeAmounts]);

  const addProduct = () => {
    const newProduct = {
      id: 0,
      product_line_code: '001',
      amount: {
        currency_code: CURRENCY_CODES.ars,
        value: 0,
      },
    };

    productFormik.setValues([...productFormik.values, newProduct]);
    setApplicationInfo((prevData) => [...prevData, newProduct]);
  };

  const removeProduct = (index: number) => {
    setApplicationInfo((prevData) => prevData.filter((_, i) => i !== index));

    productFormik.setValues(productFormik.values.filter((_, i) => i !== index));
    updateTotals();
    setIsdeleteApplication(false);
  };

  const updateTotals = () => {
    let totalArsTemp = 0;
    let totalUsdTemp = 0;

    productFormik.values.forEach((product: applicationData) => {
      const amount = toNumber(product.amount.value) || 0;
      product.amount.currency_code === CURRENCY_CODES.ars
        ? (totalArsTemp += amount)
        : (totalUsdTemp += amount);
    });

    setTotalArs(totalArsTemp);
    setTotalUsd(totalUsdTemp);
  };

  const isApplicationEditable = () => {
    return !isRejected && !isFinished && !isApproved && !isInCommittee && !isInInstrumentation;
  };

  useEffect(() => {
    if (applications?.length > 0) {
      productFormik.setValues(applications);
    } else {
      const defaultProduct = {
        id: 0,
        product_line_code: '001',
        amount: {
          currency_code: CURRENCY_CODES.ars,
          value: 0,
        },
      };

      productFormik.setValues([defaultProduct]);
      setApplicationInfo([defaultProduct]);
    }
    if (requestedAmounts.length > 0) {
      setManualAmounts(requestedAmounts);
    }

    if (manualTotalAmounts) {
      setsummarizeAmounts(!manualTotalAmounts);
    }
  }, [applications]);

  const handleInputValue = (
    summarizeAmounts: boolean,
    manualAmounts: amount[],
    currencyCode: string
  ) => {
    const amount = manualAmounts?.find(
      (item) => item.currency_code === currencyCode
    )?.value;

    if (amount === '' || amount === undefined) return '';

    if (typeof amount === 'string') {
      return toNumber(amount);
    }
    return amount;
  };

  const handleToggleChange = () => {
    if (summarizeAmounts) {
      setManualAmounts([
        {
          currency_code: CURRENCY_CODES.ars,
          value: 0,
        },
        {
          currency_code: CURRENCY_CODES.usd,
          value: 0,
        },
      ]);
    }
    setsummarizeAmounts(!summarizeAmounts);
  };
  
  const handleSaveWithSignersFiles = async (data: any) => {
    try {
      setIsUploadingSignersFiles(true);
      
      // Si hay archivos pendientes de firmantes, subirlos primero
      if (signersUploadFunction) {
        await signersUploadFunction();
      }
      
      // Luego guardar los datos
      onSave(data);
    } catch (error) {
      console.error('Error al subir archivos de firmantes:', error);
      // Aquí podrías mostrar un toast de error
    } finally {
      setIsUploadingSignersFiles(false);
    }
  };

  const handleSignersChange = (e: {
    values: signersData[];
    hasErrors: boolean;
    hasPendingFiles?: boolean;
    uploadPendingFiles?: () => Promise<void>;
  }) => {
    setClientInfo((prev) => ({
      ...prev,
      signers: e.values,
    }));
    setHasErrors(e.hasErrors);
    
    // Guardar la función de subida de archivos si existe
    if (e.uploadPendingFiles) {
      setSignersUploadFunction(() => () => e.uploadPendingFiles!());
    }
  };
  return (
    <Card>
      <Modal
        isOpen={isDeleteApplicationOpen}
        onClose={() => setIsdeleteApplication(false)}
        title={'Eliminar producto'}
        primaryText='Eliminar'
        primaryVariant='danger'
        secondaryText='Cancelar'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setIsdeleteApplication(false), setModalText('');
        }}
        onPrimaryAction={() => removeProduct(productToDelete)}
        children={
          <P type='1' variant='neutral'>
            ¿Estás seguro de que querés eliminar el producto?
          </P>
        }
      />
      {productLines && (
        <Card spacing={{ left: '6' }}>
          <P type='1' variant='neutral-1' weight='bold' modifier='strong'>
            Solicitud
          </P>
          <form onSubmit={productFormik.handleSubmit}>
            {productFormik.values.map(
              (product: applicationData, index: number) => (
                <Card
                  key={product.id}
                  display='flex'
                  margin={{ bottom: '2' }}
                  alignItems='baseline'
                  width='100%'
                  gap='16px'
                >
                  <Card width='55%' display='grid' gap='8px'>
                    {index === 0 && (
                      <P
                        type='2'
                        withMargin={false}
                        variant='neutral'
                        weight='bold'
                        modifier='strong'
                      >
                        Producto
                      </P>
                    )}
                    <DropDown
                      size='md'
                      id='application-product-line-dropdown'
                      items={productLines}
                      title={
                        productLines.find(
                          (item: any) =>
                            item.code ===
                            productFormik.values[index].product_line_code
                        )?.label
                      }
                      onChange={(e) =>
                        productFormik.setFieldValue(
                          `[${index}].product_line_code`,
                          productLines.find((item: any) => item.label === e)
                            .code
                        )
                      }
                      disabled={!isApplicationEditable()}
                    />
                    <Span variant='danger' type='2'>
                      {productFormik.errors[index]?.product_line_code}
                    </Span>
                  </Card>
                  <Card display='flex' gap='16px' width='45%'>
                    <Card width='170px' display='grid' gap='8px'>
                      {index === 0 && (
                        <P
                          type='2'
                          withMargin={false}
                          variant='neutral'
                          weight='bold'
                          modifier='strong'
                        >
                          Moneda
                        </P>
                      )}
                      <DropDown
                        id='application-currency-dropdown'
                        items={[
                          { label: CURRENCY_CODES.ars },
                          { label: CURRENCY_CODES.usd },
                        ]}
                        title={productFormik.values[index].amount.currency_code}
                        size='md'
                        onChange={(e) =>
                          productFormik.setFieldValue(
                            `[${index}].amount.currency_code`,
                            e
                          )
                        }
                        disabled={!isApplicationEditable()}
                      />
                      <Span variant='danger' type='2'>
                        {productFormik.errors[index]?.amount?.currency_code}
                      </Span>
                    </Card>
                    <Card width='100%'>
                      {index === 0 && (
                        <P
                          type='2'
                          withMargin={false}
                          variant='neutral'
                          weight='bold'
                          modifier='strong'
                        >
                          Monto
                        </P>
                      )}
                      <Card
                        display='flex'
                        margin={{ top: '3' }}
                        alignItems='center'
                        gap='16px'
                      >
                        <NumericFormat
                          inputStatus={
                            productFormik.errors[index]?.amount
                              ? 'error'
                              : 'normal'
                          }
                          placeholder='Ingresá un monto'
                          type='text'
                          disabled={!isApplicationEditable()}
                          id='application-amount-input'
                          value={String(
                            productFormik.values[index]?.amount.value
                          )}
                          maxLength={41}
                          thousandSeparator='.'
                          decimalSeparator=','
                          allowNegative={false}
                          onValueChange={(values) =>
                            productFormik.setFieldValue(
                              `[${index}].amount.value`,
                              values.floatValue ?? ''
                            )
                          }
                          customInput={Input}
                          decimalScale={2}
                        />
                        {index !== 0 && (
                          <Button
                            size='md'
                            variant='secondary'
                            onClick={() => {
                              setIsdeleteApplication(true),
                                setProductToDelete(index);
                            }}
                            disabled={!isApplicationEditable()}
                          >
                            <Card
                              variant='none'
                              margin={{
                                top: '2',
                                left: '0',
                                bottom: '2',
                                right: '0',
                              }}
                            >
                              <Icon
                                variant='neutral'
                                name='delete_outline'
                                cursor='pointer'
                              />
                            </Card>
                          </Button>
                        )}
                      </Card>
                      <Span variant='danger' type='2'>
                        {productFormik.errors[index]?.amount}
                      </Span>
                    </Card>
                  </Card>
                </Card>
              )
            )}
            <Button
              id='application-add-button'
              variant='tertiary'
              size='md'
              onClick={addProduct}
              disabled={!productFormik.isValid || !isApplicationEditable()}
            >
              + Agregar solicitud
            </Button>
          </form>
          <Card display='flex' justifyContent='flex-end' alignItems='center'>
            <Toggle
              id='application-toggle-amounts'
              checked={summarizeAmounts}
              leftText='Ingresar monto total'
              rightText='Sumar montos'
              onChange={() => handleToggleChange()}
              disabled={!isApplicationEditable()}
            />
          </Card>
          <Card
            border={true}
            display='flex'
            variant={clientTheme ? 'primary' : 'success'}
            modifier='weak'
            spacing={'3'}
            justifyContent='space-between'
            alignItems='center'
          >
            <Card width='70%'>
              <H type='3'>Total a solicitar:</H>
            </Card>

            {summarizeAmounts ? (
              <Card width='30%'>
                <H type='3' withMargin={false} weight='bold' variant={clientTheme ? 'primary' : 'success'}>
                  {CURRENCY_CODES.ars} {formatCurrency(totalArs ?? 0, true)}
                </H>

                <H type='3' withMargin={false} weight='bold' variant={clientTheme ? 'primary' : 'success'}>
                  {CURRENCY_CODES.usd} {formatCurrency(totalUsd ?? 0, true)}
                </H>
              </Card>
            ) : (
              <Card width='30%'>
                <Card display='flex' gap='8px'>
                  <H
                    type='3'
                    withMargin={false}
                    weight='bold'
                    variant={clientTheme ? 'primary' : 'success'}
                  >
                    {CURRENCY_CODES.ars}{' '}
                  </H>
                  <NumericFormat
                    placeholder='Ingresá un monto'
                    type='text'
                    disabled={!isApplicationEditable()}
                    id='application-ars-input'
                    value={handleInputValue(
                      summarizeAmounts,
                      manualAmounts,
                      CURRENCY_CODES.ars
                    )}
                    variant='transparent'
                    maxLength={41}
                    thousandSeparator='.'
                    decimalSeparator=','
                    onValueChange={(values) =>
                      handleInputChange(
                        CURRENCY_CODES.ars,
                        values.floatValue ?? ''
                      )
                    }
                    customInput={Input}
                    decimalScale={2}
                    allowNegative={false}
                  />
                </Card>
                <Card display='flex' gap='8px'>
                  <H
                    type='3'
                    withMargin={false}
                    weight='bold'
                    variant={clientTheme ? 'primary' : 'success'}
                  >
                    {CURRENCY_CODES.usd}{' '}
                  </H>
                  <NumericFormat
                    placeholder='Ingresá un monto'
                    type='text'
                    disabled={!isApplicationEditable()}
                    id='application-usd-input'
                    value={handleInputValue(
                      summarizeAmounts,
                      manualAmounts,
                      CURRENCY_CODES.usd
                    )}
                    variant='transparent'
                    maxLength={41}
                    thousandSeparator='.'
                    decimalSeparator=','
                    onValueChange={(values) =>
                      handleInputChange(
                        CURRENCY_CODES.usd,
                        values.floatValue ?? ''
                      )
                    }
                    customInput={Input}
                    decimalScale={2}
                    allowNegative={false}
                  />
                </Card>
              </Card>
            )}
          </Card>
          {!hideInfo && (
            <Card margin={{ top: '5' }}>
              <P type='1' variant='neutral-1' weight='bold' modifier='strong'>
                Firmantes
              </P>
              <Signers
                cuit={cuit}
                signersData={signers || []}
                isApplicationEditable={!isApplicationEditable()}
                onChange={handleSignersChange}
                applicationId={id}
                corporateStructure={clientInfo.corporate_structure}
                requireValidations={!isApplicationEditable()}
                allowEmptySigners={true}
              />
              <P type='1' variant='neutral-1' weight='bold' modifier='strong'>
                Datos complementarios
              </P>

              {/* Fila 1: Fecha de vigencia y Clase de crédito */}
              {(isApplicationEffectiveDateEnabled || isApplicationCreditTypeEnabled) && (
                <Card display='flex' justifyContent='space-between' gap='16px' margin={{ top: '3' }}>
                  {isApplicationEffectiveDateEnabled && (
                    <Card margin={{ top: '3' }} width={isApplicationCreditTypeEnabled ? '50%' : '100%'}>
                      <P withMargin={false} variant='neutral' type='4' modifier='strong'>
                        Fecha de vigencia
                      </P>
                      <Card margin={{ top: '2' }}>
                        <DatePicker
                          defaultValue={applicationDataFormik.values.effective_date || ''}
                          onChange={(date) => {
                            applicationDataFormik.setFieldValue('effective_date', date);
                          }}
                          disabled={!isApplicationEditable()}
                          displayFormat="DD/MM/AA"
                          minDate={new Date()}
                        />
                      </Card>
                    </Card>
                  )}
                  {isApplicationCreditTypeEnabled && (
                    <Card width={isApplicationEffectiveDateEnabled ? '50%' : '100%'}>
                      <DropDown
                        id='application-credit-type-dropdown'
                        label='Clase de crédito *'
                        title={getSelectedCreditType()}
                        items={creditTypeItems}
                        onChange={handleCreditTypeChange}
                        size="md"
                        marginTop="4px"
                        disabled={!isApplicationEditable()}
                      />
                    </Card>
                  )}
                </Card>
              )}
              
              {/* Fila 2: Motivo y Comentarios */}
              <Card display='flex' justifyContent='space-between' gap='16px' margin={{ top: '3' }}>
                <Card width='50%'>
                  <Input
                    id='application-reason-input'
                    onChange={(e) =>
                      applicationDataFormik.setFieldValue(
                        'reason',
                        e.target.value
                      )
                    }
                    label='Motivo'
                    placeholder='Ingresá el motivo de la solicitud'
                    value={applicationDataFormik.values.reason}
                    disabled={!isApplicationEditable()}
                  />
                </Card>
                <Card width='50%'>
                  <Input
                    id='application-extra-comment-input'
                    value={applicationDataFormik.values.extraComment}
                    onChange={(e) =>
                      applicationDataFormik.setFieldValue(
                        'extraComment',
                        e.target.value
                      )
                    }
                    label='Comentarios'
                    placeholder='Comentarios adicionales'
                    disabled={!isApplicationEditable()}
                  />
                </Card>
              </Card>
              
              {/* Fila 3: Reseñas (full width) */}
              <Card margin={{ top: '3' }}>
                <TextArea
                  id='application-review-input'
                  maxLength={500}
                  value={applicationDataFormik.values.review}
                  onChange={(e) =>
                    applicationDataFormik.setFieldValue(
                      'review',
                      e.target.value
                    )
                  }
                  placeHolder='Ingresá una breve reseña de la empresa'
                  label='Reseñas'
                  disabled={!isApplicationEditable()}
                />
              </Card>
            </Card>
          )}
          {!hideInfo && (
            <Card
              display='flex'
              justifyContent='space-between'
              margin={{ top: '5' }}
            >
              <Card width='200px'>
                <Button
                  id='client-button-back'
                  size='lg'
                  variant='secondary'
                  onClick={() => onPreviousClick()}
                  fullWidth
                >
                  Volver
                </Button>
              </Card>
              <Card
                display='flex'
                justifyContent='flex-end'
                gap='16px'
                width='420px'
              >
                <Button
                  id='client-button-save'
                  size='lg'
                  variant='secondary'
                  onClick={() =>
                    handleSaveWithSignersFiles({
                      applications: productFormik.values,
                      manualTotalAmounts: !summarizeAmounts,
                      requestedAmounts: manualAmounts,
                      reason: applicationDataFormik.values.reason,
                      review: applicationDataFormik.values.review,
                      extraComment: applicationDataFormik.values.extraComment,
                      corporateStructure: clientInfo.corporate_structure,
                      signers: clientInfo.signers,
                      effective_date: applicationDataFormik.values.effective_date,
                      credit_type_id: applicationDataFormik.values.credit_type_id,
                      credit_type_name: applicationDataFormik.values.credit_type_name,
                    })
                  }
                  disabled={
                    applicationStatus === APPLICATION_STATUS.rejected.code ||
                    applicationStatus === APPLICATION_STATUS.approved.code ||
                    applicationStatus === APPLICATION_STATUS.finished.code ||
                    isSaving ||
                    isUploadingSignersFiles ||
                    hasErrors
                  }
                  fullWidth
                >
                  {isSaving || isUploadingSignersFiles ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Guardar'
                  )}
                </Button>
                <Button
                  id='client-button-continue'
                  size='lg'
                  variant='primary'
                  onClick={() =>
                    handleSaveWithSignersFiles({
                      applications: productFormik.values,
                      shouldContinue: hasErrors === false,
                      manualTotalAmounts: !summarizeAmounts,
                      requestedAmounts: manualAmounts,
                      reason: applicationDataFormik.values.reason,
                      review: applicationDataFormik.values.review,
                      extraComment: applicationDataFormik.values.extraComment,
                      corporateStructure: clientInfo.corporate_structure,
                      signers: clientInfo.signers,
                      effective_date: applicationDataFormik.values.effective_date,
                      credit_type_id: applicationDataFormik.values.credit_type_id,
                      credit_type_name: applicationDataFormik.values.credit_type_name,
                    })
                  }
                  fullWidth
                  disabled={
                    !productFormik.isValid || 
                    isSaving || 
                    isUploadingSignersFiles || 
                    (hasErrors && clientInfo.signers.length > 0) ||
                    (isApplicationCreditTypeEnabled && !applicationDataFormik.values.credit_type_id && !applicationDataFormik.values.credit_type_name)
                  }
                >
                  {isSaving || isUploadingSignersFiles ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Continuar'
                  )}
                </Button>
              </Card>
            </Card>
          )}
        </Card>
      )}
    </Card>
  );
};

export default Application;
